import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { useIntelligentAssignment } from "@/hooks/useReviewAssignment";
import { Zap, Users, BarChart3 } from "lucide-react";

export function IntelligentAssignmentEngine({ workspaceId }: { workspaceId: string }) {
  const [strategy, setStrategy] = useState<string>("balanced");
  const [autoAssign, setAutoAssign] = useState(true);
  const [loadBalancing, setLoadBalancing] = useState(80);
  const assignTasks = useIntelligentAssignment();

  const handleAssign = async () => {
    // Note: This is a simplified version - actual implementation would need taskId
    // For demo purposes, showing the UI structure
    console.log("Assignment strategy:", strategy);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Intelligent Assignment Engine
        </CardTitle>
        <CardDescription>
          Automatically assign bills to reviewers based on skills, workload, and performance
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Assignment Strategy</Label>
            <Select value={strategy} onValueChange={setStrategy}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="balanced">Balanced Load</SelectItem>
                <SelectItem value="skill_based">Skill-Based</SelectItem>
                <SelectItem value="priority_first">Priority First</SelectItem>
                <SelectItem value="round_robin">Round Robin</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {strategy === "balanced" && "Distribute work evenly across all reviewers"}
              {strategy === "skill_based" && "Match bills to reviewers with relevant expertise"}
              {strategy === "priority_first" && "Assign high-priority bills to top performers"}
              {strategy === "round_robin" && "Simple rotation through available reviewers"}
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Auto-Assignment</Label>
              <p className="text-xs text-muted-foreground">
                Automatically assign bills as they enter manual review
              </p>
            </div>
            <Switch checked={autoAssign} onCheckedChange={setAutoAssign} />
          </div>

          <div className="space-y-2">
            <Label>Load Balancing Threshold: {loadBalancing}%</Label>
            <Slider
              value={[loadBalancing]}
              onValueChange={([value]) => setLoadBalancing(value)}
              min={50}
              max={100}
              step={5}
            />
            <p className="text-xs text-muted-foreground">
              Stop assigning when reviewer reaches {loadBalancing}% of capacity
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">Active Reviewers</p>
              </div>
              <p className="text-2xl font-bold">12</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">Avg Workload</p>
              </div>
              <p className="text-2xl font-bold">68%</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">Unassigned</p>
              </div>
              <p className="text-2xl font-bold">24</p>
            </CardContent>
          </Card>
        </div>

        <Button onClick={handleAssign} disabled={assignTasks.isPending} className="w-full">
          <Zap className="h-4 w-4 mr-2" />
          Run Assignment Engine
        </Button>
      </CardContent>
    </Card>
  );
}
